import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  Box,
  Heading3,
  Display,
  FlexDirection,
  ModalBody,
  JustifyContent,
  Overflow,
  ModalClose,
  DisplayPosition,
  Divider,
  Color,
} from '@gousto-internal/citrus-react'
import { Recipe, RecipeUUID } from '@library/menu-service'
import { Recipes } from './Recipes'
import { DeliveryDetails } from './DeliveryDetails'
import { CheckoutButton } from './CheckoutButton'
import { OrderSummary } from './OrderSummary'
import type { DeliveryPricing } from './types'
import { TransactionalOrderBanner } from './TransactionalOrderBanner'
import { BasketUpsellSection } from './BasketUpsell/BasketUpsellSection'
import type {
  BasketUpsellCarouselContent,
  BasketUpsellProduct,
  BasketUpsellProductActionSource,
} from './BasketUpsell/types'
import { Products } from './Products'
import { UndoBanner } from '../UndoBanner/UndoBanner'

type BasketUpsell = {
  carousel: BasketUpsellCarouselContent
  products: BasketUpsellProduct[]
  selectedProducts: BasketUpsellProduct[]
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onClickProduct: (product: BasketUpsellProduct) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

type ContentProps = {
  name: string
  maxRecipesInBox: number
  numPortions: number
  basketRecipes: {
    recipe: Recipe
    quantity: number
  }[]
  closeModal: () => void
  addRecipe: (id: RecipeUUID, view: string) => void
  removeRecipe: (id: RecipeUUID, view: string) => void
  onClickRecipe: (id: RecipeUUID) => void
  onCheckout: (e: SyntheticEvent) => void
  deliverySlot: {
    date: string
    timeStart: string
    timeEnd: string
  }
  deliveryAddress: {
    name?: string
    line1: string
    line2: string
    line3: string
    town: string
    postcode: string
    state: string
    county: string
  }
  deliveryPricing: DeliveryPricing
  isCheckoutDisabled: boolean
  isCheckoutPending: boolean
  isTransactionalOrder: boolean
  basketUpsell?: BasketUpsell
}

export const Content = ({
  name,
  maxRecipesInBox,
  numPortions,
  basketRecipes,
  closeModal,
  addRecipe,
  removeRecipe,
  onClickRecipe,
  onCheckout,
  deliverySlot,
  deliveryAddress,
  deliveryPricing,
  isCheckoutDisabled,
  isCheckoutPending,
  isTransactionalOrder,
  basketUpsell,
}: ContentProps) => {
  const [lastRemoved, setLastRemoved] = useState<Recipe | null>(null)
  const bannerTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const clearLastRemoved = () => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
      bannerTimeoutId.current = null
    }
    setLastRemoved(null)
  }

  const scheduleAutoHide = () => {
    bannerTimeoutId.current = setTimeout(clearLastRemoved, 3000)
  }

  const handleRemove = (recipe: Recipe) => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
    }
    setLastRemoved(recipe)
    scheduleAutoHide()
    removeRecipe(recipe.id, 'modal_basket')
  }

  const handleUndo = () => {
    if (!lastRemoved) {
      return
    }
    addRecipe(lastRemoved.id, 'modal_basket')
    clearLastRemoved()
  }

  useEffect(() => () => clearLastRemoved(), [])

  const numberOfRecipesInBasket = basketRecipes.reduce((total, item) => total + item.quantity, 0)
  const isOverLimitBy = numberOfRecipesInBasket - maxRecipesInBox

  return (
    <>
      <Box paddingH={5}>
        <ModalClose onClose={closeModal} />
        <Box position={DisplayPosition.Relative} paddingH={0} paddingBottom={3}>
          <Heading3>{`${name}'s basket`}</Heading3>
        </Box>
      </Box>
      <Divider />
      <ModalBody paddingV={0} paddingH={0}>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.FlexStart}
          flexGrow={1}
          minHeight={['60vh']}
          paddingH={5}
          paddingV={4}
          gap={6}
          bg={Color.NeutralGrey_50}
          overflowY={Overflow.Scroll}
        >
          {isTransactionalOrder && <TransactionalOrderBanner />}
          <Recipes
            basketRecipes={basketRecipes}
            addRecipe={addRecipe}
            removeRecipe={handleRemove}
            onClickRecipe={onClickRecipe}
            numberOfRecipesInBasket={numberOfRecipesInBasket}
            numPortions={numPortions}
          />
          {basketUpsell && (
            <>
              <BasketUpsellSection
                carousel={basketUpsell.carousel}
                products={basketUpsell.products}
                onAddProduct={basketUpsell.onAddProduct}
                onClickProduct={basketUpsell.onClickProduct}
                onRemoveProduct={basketUpsell.onRemoveProduct}
              />
              <Products
                products={basketUpsell.selectedProducts}
                onAddProduct={basketUpsell.onAddProduct}
                onClickProduct={basketUpsell.onClickProduct}
                onRemoveProduct={basketUpsell.onRemoveProduct}
              />
            </>
          )}
          <OrderSummary
            deliveryPricing={deliveryPricing}
            numberOfRecipes={numberOfRecipesInBasket}
            shouldShowOrderSummary={numberOfRecipesInBasket <= maxRecipesInBox}
          />
          <DeliveryDetails deliverySlot={deliverySlot} deliveryAddress={deliveryAddress} />
        </Box>
      </ModalBody>
      <Box
        display={Display.Flex}
        width="100%"
        justifyContent={JustifyContent.Center}
        boxShadow="0px -2px 4px rgba(0, 0, 0, 0.15)"
        style={{
          position: 'sticky',
          paddingTop: '1rem',
          paddingBottom: '1.5rem',
        }}
        bg={Color.White}
        bottom="0"
        left="0"
        right="0"
      >
        {lastRemoved && (
          <Box
            position={DisplayPosition.Absolute}
            width="400px"
            top="-50px"
            left="50%"
            style={{ transform: 'translateX(-50%)' }}
          >
            <UndoBanner recipe={lastRemoved} onUndo={handleUndo} />
          </Box>
        )}
        <CheckoutButton
          isOverLimitBy={isOverLimitBy}
          maxRecipesInBox={maxRecipesInBox}
          onCheckout={onCheckout}
          isCheckoutDisabled={!!lastRemoved || isCheckoutDisabled}
          isCheckoutPending={isCheckoutPending}
        />
      </Box>
    </>
  )
}
